import { Heart, MapPin, ShoppingBag } from "lucide-react";
import { StarRating } from "@/components/StarRating";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { inr } from "@/lib/api";

const CONDITION_STYLES = {
  "Like New": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Good: "bg-blue-50 text-blue-700 border-blue-200",
  Fair: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function ListingCard({ listing, onOpen, onFavorite }) {
  const sold = listing.status === "sold";
  return (
    <div
      className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 hover:shadow-[0_8px_30px_rgba(15,23,42,0.08)] transition-all duration-200 flex flex-col"
      data-testid={`listing-card-${listing.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img
          src={listing.images?.[0]}
          alt={listing.title}
          loading="lazy"
          onClick={() => onOpen(listing)}
          className="h-full w-full object-cover cursor-pointer group-hover:scale-[1.03] transition-transform duration-300"
        />
        <span className={`absolute top-3 left-3 text-[11px] font-medium px-2 py-0.5 rounded-md border ${CONDITION_STYLES[listing.condition] || "bg-white text-slate-600 border-slate-200"}`}>
          {listing.condition}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onFavorite(listing); }}
          className="absolute top-2.5 right-2.5 h-8 w-8 grid place-items-center rounded-full bg-white/90 backdrop-blur border border-slate-200 hover:scale-110 transition-transform"
          data-testid={`favorite-btn-${listing.id}`}
          aria-label="Toggle favorite"
        >
          <Heart size={16} className={listing.is_favorite ? "fill-crimson text-crimson" : "text-slate-500"} />
        </button>
        {sold && (
          <div className="absolute inset-0 bg-ink/55 grid place-items-center pointer-events-none">
            <span className="label-mono text-white text-sm bg-crimson px-3 py-1 rounded">SOLD</span>
          </div>
        )}
        {listing.bought_count > 15 && !sold && (
          <span className="absolute bottom-3 left-3 label-mono text-[9px] bg-ink text-white px-2 py-1 rounded-md">
            🔥 Most bought
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-semibold text-[15px] text-ink leading-snug line-clamp-2 cursor-pointer hover:text-brand" onClick={() => onOpen(listing)}>
            {listing.title}
          </h3>
        </div>
        <div className="mt-1.5 font-display font-extrabold text-lg text-ink">{inr(listing.price)}</div>

        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
          <MapPin size={12} className="text-slate-400 shrink-0" />
          <span className="truncate">{listing.pickup_point?.name || "Campus"}</span>
        </div>

        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Avatar className="h-6 w-6">
              <AvatarImage src={listing.seller?.avatar} />
              <AvatarFallback className="text-[10px]">{listing.seller?.name?.[0]}</AvatarFallback>
            </Avatar>
            <span className="text-xs text-slate-600 truncate">{listing.seller?.name}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <StarRating value={listing.seller?.rating_avg} size={12} />
            <span className="text-xs font-semibold text-ink">{listing.seller?.rating_avg?.toFixed(1)}</span>
          </div>
        </div>

        <button
          onClick={() => onOpen(listing)}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 h-9 rounded-lg bg-slate-100 hover:bg-ink hover:text-white text-ink text-sm font-medium transition-colors"
          data-testid={`view-chat-btn-${listing.id}`}
        >
          <ShoppingBag size={15} /> View & Chat
        </button>
      </div>
    </div>
  );
}
